import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Minus } from "lucide-react";
import Chip from "./ui/Chip";
import CustomTooltip from "./Tooltip";
import { dataService } from "../../Services/data/data.service";

// Shared price-cell rendering for the various DevExtreme tables that display
// a measurement's eventPrice (optionally with a projection chip). Hovering the
// price reveals a per-PriceDefinition breakdown tooltip.
//
// Data sources, in priority order:
//   1. Per-measurement junction overrides (ProjectDetailMeasurementPriceDefinition)
//      fetched lazily via `dataService.getMeasurementPriceDefinitions` and cached.
//   2. The PriceBundle attached to the measurement (resolved from `allPriceBundles`).
//
// The fetch is kicked off via `useEffect` on mount of the cell, so under
// DevExtreme's row virtualization only currently-visible rows hit the API,
// and the data is ready by the time the user hovers.

const formatPrice = (price) =>
  price
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");

// Builds the bundle/projection lookup maps from raw props. Stable across
// renders that don't change the inputs.
export function useBundleMaps(allPriceBundles, activeProjection) {
  const bundleById = useMemo(() => {
    const map = new Map();
    if (!allPriceBundles) return map;
    for (const company of allPriceBundles) {
      if (!company?.priceBundles) continue;
      for (const b of company.priceBundles) map.set(b.id, b);
    }
    return map;
  }, [allPriceBundles]);

  const projectedPriceByDefId = useMemo(() => {
    const map = new Map();
    const pds = activeProjection?.priceDefinitions;
    if (!pds) return map;
    for (const pd of pds) map.set(pd.priceDefinitionId, pd.newPrice);
    return map;
  }, [activeProjection]);

  const bundleTotalsById = useMemo(() => {
    const map = new Map();
    for (const [id, bundle] of bundleById) {
      if (!bundle?.items || bundle.items.length === 0) continue;
      let historical = 0;
      let projected = 0;
      for (const it of bundle.items) {
        historical += it.price * it.multiplier;
        const projUnit = projectedPriceByDefId.has(it.priceDefinitionId)
          ? projectedPriceByDefId.get(it.priceDefinitionId)
          : it.price;
        projected += projUnit * it.multiplier;
      }
      map.set(id, { historical, projected });
    }
    return map;
  }, [bundleById, projectedPriceByDefId]);

  return { bundleById, bundleTotalsById, projectedPriceByDefId };
}

// Holds the per-measurement junction-breakdown cache. Values:
//   undefined -> not yet fetched
//   "pending" -> fetch in flight
//   []        -> measurement uses bundle only (no junction rows)
//   [items]   -> resolved junction rows from the API
export function useMeasurementBreakdownCache() {
  const [cache, setCache] = useState(() => new Map());
  const ensure = useCallback((measurementId) => {
    if (!measurementId) return;
    setCache((prev) => {
      if (prev.has(measurementId)) return prev;
      const next = new Map(prev);
      next.set(measurementId, "pending");
      dataService.getMeasurementPriceDefinitions(measurementId).then((result) => {
        setCache((cur) => {
          const upd = new Map(cur);
          upd.set(measurementId, Array.isArray(result) ? result : []);
          return upd;
        });
      });
      return next;
    });
  }, []);
  return { cache, ensure };
}

export function PriceCell({
  row,
  currency,
  classes,
  bundleById,
  bundleTotalsById,
  projectedPriceByDefId,
  breakdownCache,
  ensureBreakdown,
}) {
  const projectedPrice = row?.row?.projectedEventPrice;
  const isProfit = row?.row?.isProfit;
  const measurementId = row?.row?.id;

  useEffect(() => {
    if (measurementId) ensureBreakdown(measurementId);
  }, [measurementId, ensureBreakdown]);

  if (row.value === null || row.value === undefined || row.value === "") {
    return "-";
  }

  // Group rows in DevExDataTable: no per-row data, just display the value.
  if (!row.row) {
    return (
      <p className={classes.eventPrice}>
        {currency.symbol}
        {formatPrice(row.value)}
      </p>
    );
  }

  const getChipVariant = () => {
    if (isProfit && projectedPrice > row.value) return "success";
    if (!isProfit && projectedPrice > row.value) return "danger";
    if (isProfit && projectedPrice < row.value) return "danger";
    if (!isProfit && projectedPrice < row.value) return "success";
    return undefined;
  };

  const hasProjection =
    projectedPrice !== null &&
    projectedPrice !== undefined &&
    projectedPrice !== "" &&
    Math.abs(projectedPrice) !== Math.abs(row.value);

  const cachedJunction = measurementId ? breakdownCache.get(measurementId) : undefined;
  const junctionItems = Array.isArray(cachedJunction) && cachedJunction.length > 0 ? cachedJunction : null;
  const junctionPending = cachedJunction === undefined || cachedJunction === "pending";

  const bundle = row?.row?.priceBundleId && bundleById ? bundleById.get(row.row.priceBundleId) : null;
  const totals = bundle && bundleTotalsById ? bundleTotalsById.get(bundle.id) : null;

  const buildJunctionHistorical = () => {
    if (!junctionItems) return null;
    return junctionItems.map((it) => ({
      name: it.priceDefinitionName || it.priceDefinitionCode || "Unnamed",
      unitPrice: Number(it.unitPrice) || 0,
      multiplier: Number(it.multiplier) || 0,
      total: Number(it.total) || 0,
    }));
  };

  const buildJunctionProjected = () => {
    if (!junctionItems || !projectedPriceByDefId) return null;
    return junctionItems.map((it) => {
      const projUnit = projectedPriceByDefId.has(it.priceDefinitionId)
        ? projectedPriceByDefId.get(it.priceDefinitionId)
        : Number(it.unitPrice) || 0;
      const multiplier = Number(it.multiplier) || 0;
      return {
        name: it.priceDefinitionName || it.priceDefinitionCode || "Unnamed",
        unitPrice: projUnit,
        multiplier,
        total: projUnit * multiplier,
      };
    });
  };

  const buildBundleBreakdown = (totalPrice, useProjected) => {
    if (!bundle || !totals || !totalPrice) return null;
    const denom = useProjected ? totals.projected : totals.historical;
    if (denom <= 0) return null;
    const out = new Array(bundle.items.length);
    for (let i = 0; i < bundle.items.length; i++) {
      const it = bundle.items[i];
      const unitPrice = useProjected && projectedPriceByDefId && projectedPriceByDefId.has(it.priceDefinitionId)
        ? projectedPriceByDefId.get(it.priceDefinitionId)
        : it.price;
      const share = (unitPrice * it.multiplier) / denom;
      out[i] = {
        name: it.priceDefinitionName || "Unnamed",
        unitPrice,
        multiplier: it.multiplier,
        total: totalPrice * share,
      };
    }
    return out;
  };

  const renderBreakdownTooltip = (rows, headerLabel) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: "220px" }}>
      {headerLabel && <p style={{ fontSize: "13px", opacity: 0.7, margin: 0 }}>{headerLabel}</p>}
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "12px", fontSize: "14px" }}>
          <span style={{ opacity: 0.9 }}>{r.name}</span>
          <span style={{ whiteSpace: "nowrap" }}>
            {currency.symbol}
            {formatPrice(r.total)}
          </span>
        </div>
      ))}
    </div>
  );

  const historicalBreakdown = buildJunctionHistorical() || buildBundleBreakdown(Math.abs(row.value), false);
  const projectedBreakdown = hasProjection
    ? (buildJunctionProjected() || buildBundleBreakdown(Math.abs(projectedPrice), true))
    : null;

  const loadingContent = (
    <div style={{ fontSize: "14px", opacity: 0.7, minWidth: "120px" }}>Loading…</div>
  );

  const tooltipPossible = !!historicalBreakdown || junctionPending;

  const historicalPriceEl = (
    <p className={classes.eventPrice} style={hasProjection ? { fontSize: ".75rem" } : undefined}>
      {currency.symbol}
      {formatPrice(row.value)}
    </p>
  );
  const wrappedHistoricalPrice = tooltipPossible
    ? (
      <CustomTooltip content={historicalBreakdown ? renderBreakdownTooltip(historicalBreakdown) : loadingContent}>
        {historicalPriceEl}
      </CustomTooltip>
    )
    : historicalPriceEl;

  const projectedChip = hasProjection ? (
    <Chip size={"small"} variant={getChipVariant()}>
      {currency.symbol}
      {formatPrice(projectedPrice)}
    </Chip>
  ) : null;
  const wrappedProjectedChip = projectedChip && (projectedBreakdown || junctionPending)
    ? (
      <CustomTooltip content={projectedBreakdown ? renderBreakdownTooltip(projectedBreakdown, "Projected") : loadingContent}>
        {projectedChip}
      </CustomTooltip>
    )
    : projectedChip;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "flex-end" }}>
      {isProfit !== null && isProfit ? (
        row.row.isNegative ? (
          <Minus size={16} color={"#D84949"} />
        ) : (
          <Plus size={16} color={"#5CC958"} />
        )
      ) : row.row.isNegative ? (
        <Plus size={16} color={"#5CC958"} />
      ) : (
        <Minus size={16} color={"#D84949"} />
      )}
      {hasProjection ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "1rem" }}>
          {wrappedHistoricalPrice}
          {wrappedProjectedChip}
        </div>
      ) : (
        wrappedHistoricalPrice
      )}
    </div>
  );
}
